import React from "react";
import { CONTACT_US_FROM_HEADER_PAGE_LINK, LOGIN_PAGE_LINK, PRICING_PAGE_LINK } from "constants/links";
import Button from "segments/desktop/core-components/button/button";
import { CSSTransitionGroup } from "react-transition-group-community-version";
import classnames from "classnames";
import {
  shouldRenderHeaderWithoutMenuAndContactSalesTest,
  shouldRenderBrandCampaignHeaderTest
} from "services/ab-tests/acquisition-ab-test-service";
import { SIGNUP_BUTTON_TYPE } from "segments/desktop/core-components/button/button-consts";
import { withAbTests } from "contexts/ab-tests-context";
import { withRouter } from "next/router";
import { getUrlParamValue } from "utils/url";
import { HEADER_WITHOUT_MENU_AND_WITH_CONTACT_SALES_TEST, BRAND_CAMPAIGN_HEADER_TEST } from "constants/new-ab-tests";
import { isInTest } from "services/new-ab-tests/new-ab-tests-service";
import LinkItemComponent from "../link-item/link-item-component";
import LinkItemWithMenuComponent from "../link-item-with-menu/link-item-with-menu-component";
import {
  FEATURES_MENU_ID,
  PRODUCTS_MENU_ID,
  RESOURCES_MENU_ID,
  SectionsHeadersMenuItemsMapping,
  SectionsLinkWithMenuMenuComponentMapping,
  USECASES_MENU_ID
} from "../../base-header-service";
import HeaderConstants from "../../constants";
import HeaderMenus from "../header-menu";

const UniverseHeader = (props) => {
  const {
    showMenu,
    showButton,
    buttonConfig,
    shouldShowMainPricingLink,
    isSecondaryMenuNeeded,
    renderUnifiedLinks,
    openSignupPopup,
    showContactSales,
    abTests,
    router,
    simulateBrandCampaign
  } = props;
  const { openedMenuId, openLinkMenu, closeLinkMenu } = props;

  const openProductsMenu = () => openLinkMenu(PRODUCTS_MENU_ID);
  const openWorkflowsMenu = () => openLinkMenu(USECASES_MENU_ID);
  const openFeaturesMenu = () => openLinkMenu(FEATURES_MENU_ID);
  const openResourcesMenu = () => openLinkMenu(RESOURCES_MENU_ID);

  const isMenuOpen = (menuId) => {
    return openedMenuId === menuId;
  };

  const renderBaseHeaderLeftLinks = () => {
    return (
      <>
        <div
          className={classnames("link-item-with-menu-component-wrapper", { "is-open": isMenuOpen(PRODUCTS_MENU_ID) })}
          role="listitem"
        >
          <LinkItemWithMenuComponent
            title="header.products"
            openMenu={openProductsMenu}
            closeMenu={closeLinkMenu}
            isOpen={isMenuOpen(PRODUCTS_MENU_ID)}
          />
        </div>
        {renderSubmenuWithTransitionIfNeeded(PRODUCTS_MENU_ID)}

        <div
          className={classnames("link-item-with-menu-component-wrapper", { "is-open": isMenuOpen(USECASES_MENU_ID) })}
          role="listitem"
        >
          <LinkItemWithMenuComponent
            title="header.useCases"
            openMenu={openWorkflowsMenu}
            closeMenu={closeLinkMenu}
            isOpen={isMenuOpen(USECASES_MENU_ID)}
          />
        </div>
        {renderSubmenuWithTransitionIfNeeded(USECASES_MENU_ID)}

        <div
          className={classnames("link-item-with-menu-component-wrapper", { "is-open": isMenuOpen(FEATURES_MENU_ID) })}
          role="listitem"
        >
          <LinkItemWithMenuComponent
            title="header.features"
            openMenu={openFeaturesMenu}
            closeMenu={closeLinkMenu}
            isOpen={isMenuOpen(FEATURES_MENU_ID)}
          />
        </div>
        {renderSubmenuWithTransitionIfNeeded(FEATURES_MENU_ID)}

        <div
          className={classnames("link-item-with-menu-component-wrapper", { "is-open": isMenuOpen(RESOURCES_MENU_ID) })}
          role="listitem"
        >
          <LinkItemWithMenuComponent
            title="header.resources"
            openMenu={openResourcesMenu}
            closeMenu={closeLinkMenu}
            isOpen={isMenuOpen(RESOURCES_MENU_ID)}
          />
        </div>
        {renderSubmenuWithTransitionIfNeeded(RESOURCES_MENU_ID)}
      </>
    );
  };

  const renderSubmenuWithTransitionIfNeeded = (menuId) => {
    // always render transition - menu rendered only if open
    const isMenuOpen = openedMenuId === menuId;

    return (
      <CSSTransitionGroup
        transitionName="submenu"
        transitionEnterTimeout={HeaderConstants.HEADER_MENU_TRANSITION_DURATION}
        transitionLeaveTimeout={HeaderConstants.HEADER_MENU_TRANSITION_DURATION}
      >
        {isMenuOpen && renderMainHeaderPopup(menuId)}
      </CSSTransitionGroup>
    );
  };

  const renderMainHeaderPopup = (menuId) => {
    const headerMenuItems = SectionsHeadersMenuItemsMapping[menuId];
    const MenuComponent = HeaderMenus[SectionsLinkWithMenuMenuComponentMapping[menuId]];

    return (
      <div className={HeaderConstants.HEADER_MENU_COMPONENT_WRAPPER_CLASS_NAME} key={menuId}>
        <nav aria-label="main submenu" className="menu-component-wrapper">
          {MenuComponent && <MenuComponent menuItems={headerMenuItems} menuId={menuId} />}
        </nav>
      </div>
    );
  };

  const isEligibleToAcquisitionContactSalesTest = () => {
    /* TEST EXCLUSIONS */

    // if show menu config was deliberately set to true on the page config - not eligible for acquisition contact sales test
    if (showMenu) {
      return false;
    }

    // if show contact sales config was deliberately set to true on the page config - not eligible for acquisition contact sales test
    if (showContactSales) {
      return false;
    }

    // if button is not shown or its type is not signup - not eligible for acquisition contact sales test
    if (!showButton || buttonConfig?.buttonType !== SIGNUP_BUTTON_TYPE) {
      return false;
    }

    return isInTest(HEADER_WITHOUT_MENU_AND_WITH_CONTACT_SALES_TEST, abTests);
  };

  const isEligibleToBrandCampaignTest = () => {
    const BRAND_CAMPAIGNS_UTM_SOURCE_PARAM_VALUES = ["adwordsbrand", "bingbrand"];

    const utmParam = getUrlParamValue(router?.asPath, "utm_source");

    // not eligible if not from brand campaign
    return (
      BRAND_CAMPAIGNS_UTM_SOURCE_PARAM_VALUES.includes(utmParam?.toLowerCase()) &&
      (simulateBrandCampaign || isInTest(BRAND_CAMPAIGN_HEADER_TEST, abTests))
    );
  };

  const buildHeaderStructure = () => {
    // init header according to its props
    const headerStructure = {
      leftLinks: { isVisible: showMenu },
      pricing: { isVisible: showMenu && shouldShowMainPricingLink() },
      contactSales: { isVisible: showMenu || showContactSales },
      login: { isVisible: showMenu },
      button: { isVisible: showButton }
    };

    /* Running Tests */
    if (isEligibleToAcquisitionContactSalesTest()) {
      // trigger split event and check for new variant
      if (shouldRenderHeaderWithoutMenuAndContactSalesTest(abTests, true)) {
        headerStructure.contactSales.isVisible = true;
      }
    } else if (isEligibleToBrandCampaignTest()) {
      // trigger split event and check for new variant
      if (simulateBrandCampaign || shouldRenderBrandCampaignHeaderTest(abTests, true)) {
        headerStructure.leftLinks = false;
        headerStructure.pricing = false;
        headerStructure.contactSales.isVisible = true;
        headerStructure.login.isVisible = true;
      }
    }

    return headerStructure;
  };

  const { leftLinks, pricing, contactSales, login, button } = buildHeaderStructure();

  return (
    <nav
      aria-label="Main"
      className={classnames("base-header-desktop-content", "universe-header-desktop-content", "products-with-tags")}
    >
      {leftLinks.isVisible && (
        <div className="left-side-header-links">
          <div className="absolute-main-links" role="list">
            {renderBaseHeaderLeftLinks()}
          </div>
          {isSecondaryMenuNeeded && <div className="absolute-unified-links">{renderUnifiedLinks()}</div>}
        </div>
      )}

      <div className="right-side-header-links">
        {pricing.isVisible && (
          <div className="link-item-component-wrapper">
            <LinkItemComponent title="header.pricing" url={PRICING_PAGE_LINK} />
          </div>
        )}

        {contactSales.isVisible && (
          <div className="link-item-component-wrapper">
            <LinkItemComponent title="header.contactSales" url={CONTACT_US_FROM_HEADER_PAGE_LINK} />
          </div>
        )}

        {login.isVisible && (
          <div className="link-item-component-wrapper">
            <LinkItemComponent title="header.logIn" url={LOGIN_PAGE_LINK} />
          </div>
        )}
      </div>

      {button.isVisible && (
        <div className="header-button-wrapper">
          <Button
            {...buttonConfig}
            parentSegment={HeaderConstants.DESKTOP_HEADER_SEGMENT_NAME}
            onButtonClick={openSignupPopup}
          />
        </div>
      )}
    </nav>
  );
};

export default withRouter(withAbTests(UniverseHeader));
